<template>
  <div data-pw="technician-block" :class="$style.technicianBlock">
    <div id="profilPicture" :class="$style.technicianPictureWrapper">
      <img :class="$style.technicianPicture" :src="technicianPicture" crossorigin="use-credentials">
    </div>
    <TextContainer>
      <BaseSpacer :mt="1" />
      <BaseHeading :semantic-level="3" color="turquoise" :class="$style.technicianName">
        {{ getTitle }}
      </BaseHeading>
      <BaseText :class="$style.technicianName" color="lightturquoise">
        {{ appointment.technician.firstName }}
      </BaseText>
    </TextContainer>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseText from '~/base/components/text/Text.vue'
import TextContainer from '~/base/components/textContainer/TextContainer.vue'
import BaseHeading from '~/base/components/heading/Heading.vue'
import BaseSpacer from '~/base/components/spacer/Spacer.vue'
import type { Appointment } from '~/terminierungsportal/types/appointmentTypes'
import { PrefixType, getGenderedTechnician } from '~/base/utils/gender'

interface BaseSummaryTechnicianProps {
  appointment: Appointment
}

const props = defineProps<BaseSummaryTechnicianProps>()

const technicianPicture = computed((): string => {
  return props.appointment.technician.picture
})

const getTitle = computed((): string => {
  return getGenderedTechnician(props.appointment.technician, PrefixType.YOUR)
})

const objectPositionXPercentage = computed(() => {
  const faceBox = props.appointment.technician.profilePicFaceBox
  const xPercentage =
    ((faceBox.x + faceBox.width / 2) / faceBox.parentDimensions.width) * 100
  return xPercentage + '%'
})

const objectPositionYPercentage = computed(() => {
  const paddingTopPercantage = 0.1
  const faceBox = props.appointment.technician.profilePicFaceBox
  const yPercentage =
    (faceBox.y / faceBox.parentDimensions.height - paddingTopPercantage) * 100
  return yPercentage + '%'
})

const transformScalePercantage = computed(() => {
  const paddingFactor = 0.2
  const faceBox = props.appointment.technician.profilePicFaceBox
  const containerWidth = 81
  const scaleFactor = (1 - containerWidth / faceBox.width - paddingFactor) * 100
  return 100 + scaleFactor + '%'
})
</script>

<style module>
.technicianBlock {
  display: flex;
  flex-direction: row;
  align-items: top;
  cursor: pointer;
  margin-top: var(--space-md);
}

.technicianPictureWrapper {
  position: relative;
  overflow: hidden;
  height: 85px;
  width: 85px;
  padding: 4px;
  border: 2px solid var(--color-turquoise);
  border-radius: 50%;
  background-clip: content-box;
  background-size: contain;
}

.technicianPicture {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: v-bind(objectPositionXPercentage) v-bind(objectPositionYPercentage);
  transform: scale(v-bind(transformScalePercantage));
}

.technicianName {
  margin-left: var(--space-xs);
}
</style>
